class CollectionList extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector(".collection-list__grid");
    this.dotsWrap = this.querySelector(".collection-list__dots");

    if (!this.grid) return;

    this.cards = this.grid.querySelectorAll(".collection-card");
    this.dots = this.dotsWrap
      ? this.dotsWrap.querySelectorAll(".collection-list__dot")
      : [];
    this.currentIndex = 0;
    this.isDown = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.isDragging = false;

    this._updateDotStates(0);
    this._bindDots();
    this._bindMouseDrag();
    this._bindTouchSnap();
    this._bindIntersectionObserver();
  }

  _bindDots() {
    this.dots.forEach((dot) => {
      dot.addEventListener("click", () => {
        const idx = parseInt(dot.dataset.index, 10);
        const card = this.cards[idx];
        if (card)
          this.grid.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
      });
    });
  }

  _setActiveDot(index) {
    this.currentIndex = index;
    this._updateDotStates(index);
  }

  _updateDotStates(activeIndex) {
    const total = this.dots.length;
    const MAX_DOTS = 6;

    if (total <= MAX_DOTS) {
      this.dots.forEach((d, i) => {
        d.classList.toggle("is-active", i === activeIndex);
        d.removeAttribute("data-state");
      });
      return;
    }

    let start = activeIndex - Math.floor(MAX_DOTS / 2);
    start = Math.max(0, Math.min(start, total - MAX_DOTS));
    const end = start + MAX_DOTS - 1;

    this.dots.forEach((d, i) => {
      d.classList.toggle("is-active", i === activeIndex);
      if (i < start || i > end) {
        d.setAttribute("data-state", "hidden");
      } else if (
        (i === start && start > 0) ||
        (i === end && end < total - 1)
      ) {
        d.setAttribute("data-state", "edge");
      } else {
        d.removeAttribute("data-state");
      }
    });
  }

  _bindMouseDrag() {
    this.grid.addEventListener("mousedown", (e) => {
      this.isDown = true;
      this.isDragging = false;
      this.startX = e.pageX - this.grid.offsetLeft;
      this.scrollLeft = this.grid.scrollLeft;
      this.grid.style.cursor = "grabbing";
      this.grid.style.userSelect = "none";
    });

    this.grid.addEventListener("mouseleave", () => {
      this.isDown = false;
      this.grid.style.cursor = "";
      this.grid.style.userSelect = "";
    });

    this.grid.addEventListener("mouseup", (e) => {
      this.isDown = false;
      this.grid.style.cursor = "";
      this.grid.style.userSelect = "";
      if (this.isDragging) {
        e.preventDefault();
        this._snapByDelta(this.grid.scrollLeft - this.scrollLeft);
      }
    });

    this.grid.addEventListener("mousemove", (e) => {
      if (!this.isDown) return;
      const walk = e.pageX - this.grid.offsetLeft - this.startX;
      if (Math.abs(walk) > 5) this.isDragging = true;
      if (this.isDragging) {
        e.preventDefault();
        this.grid.scrollLeft = this.scrollLeft - walk;
      }
    });

    this.grid.addEventListener(
      "click",
      (e) => {
        if (this.isDragging) e.preventDefault();
      },
      true,
    );
  }

  _snapByDelta(delta) {
    const threshold = (this.cards[0] ? this.cards[0].offsetWidth : this.grid.offsetWidth) * 0.15;
    let idx = this.currentIndex;
    if (delta > threshold) idx = Math.min(idx + 1, this.cards.length - 1);
    else if (delta < -threshold) idx = Math.max(idx - 1, 0);
    const card = this.cards[idx];
    if (card) this.grid.scrollTo({ left: card.offsetLeft, behavior: "smooth" });
  }

  _bindTouchSnap() {
    let touchStartX = 0;
    let touchStartScrollLeft = 0;

    this.grid.addEventListener("touchstart", (e) => {
      touchStartX = e.touches[0].clientX;
      touchStartScrollLeft = this.grid.scrollLeft;
    }, { passive: true });

    this.grid.addEventListener("touchmove", (e) => {
      e.preventDefault();
      this.grid.scrollLeft = touchStartScrollLeft + (touchStartX - e.touches[0].clientX);
    }, { passive: false });

    this.grid.addEventListener("touchend", (e) => {
      this._snapByDelta(touchStartX - e.changedTouches[0].clientX);
    }, { passive: true });
  }

  _bindIntersectionObserver() {
    if (!("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            const idx = Array.prototype.indexOf.call(this.cards, entry.target);
            if (idx !== -1) this._setActiveDot(idx);
          }
        });
      },
      {
        root: this.grid,
        threshold: 0.5,
      },
    );

    this.cards.forEach((card) => observer.observe(card));
  }
}

customElements.define("collection-list", CollectionList);
